import type { HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

export const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-colors [&_svg]:size-3",
  {
    variants: {
      variant: {
        default: "border-transparent bg-sand-200 text-aeblack-900",
        gold: "border-aegold-200 bg-aegold-50 text-aegold-800",
        solid: "border-transparent bg-primary text-white",
        outline: "border-sand-300 text-foreground bg-white/60",
        success: "border-success-100 bg-success-50 text-success-700",
        warning: "border-warning-100 bg-warning-50 text-warning-700",
        danger: "border-danger-100 bg-danger-50 text-danger-700",
        info: "border-seablue-200 bg-seablue-50 text-seablue-700",
        camel: "border-camel-200 bg-camel-50 text-camel-800",
        dark: "border-transparent bg-aeblack-950 text-white",
        ai: "border-seablue-200 bg-seablue-100 text-seablue-900",
      },
      size: {
        default: "",
        sm: "px-1.5 py-0 text-[11px]",
        lg: "px-3 py-1 text-sm",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  dot?: boolean;
  pulse?: boolean;
}

export function Badge({ className, variant, size, dot, pulse, children, ...props }: BadgeProps) {
  return (
    <span data-slot="badge" className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot ? <span className={cn("size-1.5 rounded-full bg-current", pulse && "animate-pulse")} /> : null}
      {children}
    </span>
  );
}

const statusMap: Record<string, BadgeProps["variant"]> = {
  Draft: "default",
  Submitted: "info",
  UnderReview: "gold",
  AwaitingApplicant: "warning",
  Accepted: "success",
  Published: "info",
  Investigation: "camel",
  Decision: "gold",
  Decided: "success",
  Rejected: "danger",
  Closed: "default",
  Suspended: "warning",
  Withdrawn: "default",
  OnTrack: "success",
  AtRisk: "warning",
  Breached: "danger",
  Completed: "default",
  Paused: "default",
  Proposed: "ai",
  Approved: "success",
  Edited: "success",
  Superseded: "default",
  Expired: "default",
  Low: "default",
  Medium: "info",
  High: "warning",
  Critical: "danger",
  Healthy: "success",
  Degraded: "warning",
  Down: "danger",
  Unknown: "default",
  Pending: "warning",
  Processing: "info",
  Failed: "danger",
  Paid: "success",
  Sent: "success",
  Delivered: "success",
  Admissible: "success",
  Inadmissible: "danger",
  Upheld: "danger",
  Dismissed: "default",
};

export function StatusBadge({ status, label, className, ...props }: { status: string; label?: string } & Omit<BadgeProps, "variant">) {
  return (
    <Badge variant={statusMap[status] ?? "default"} dot className={className} {...props}>
      {label ?? status}
    </Badge>
  );
}
